/**
 * Pal Game — shell + tab dispatch.
 *
 * Tabs were extracted to /app/frontend/src/components/palgame/<Tab>.jsx
 * during the June 02 2026 refactor (was 2,762 lines monolithic). This
 * file is the only thing that should grow as the game gains *new* tabs.
 *
 * Per-tab plumbing (queries, state, layout, child components) lives
 * inside each tab file. Shared atoms — PalPicker, ActionWithPal, plus
 * the `firePalgamePayout` / `firePalgameDefeat` event helpers — live
 * in ./palgame/_shared.jsx.
 *
 * Behaviour is identical to the pre-split monolith: every payout-
 * emitting tab dispatches a `chillets:palgame-payout` window event,
 * and the shared <PayoutFlash> below renders the celebration so the
 * cinematic is consistent with Gambling wins.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/Atoms";
import { PayoutFlash } from "@/components/PayoutFlash";
import {
  Swords, Sparkles, Zap, Compass, Trophy, Layers, Target, Castle,
  ArrowLeftRight, Map, Crosshair, BookOpen, Stethoscope, Coins,
} from "lucide-react";

import { CatchTab } from "@/components/palgame/CatchTab";
import { BoxTab } from "@/components/palgame/BoxTab";
import { BreedTab } from "@/components/palgame/BreedTab";
import Economy from "@/pages/Economy";
import { AdventureTab } from "@/components/palgame/AdventureTab";
import { BattleTab } from "@/components/palgame/BattleTab";
import { ArenaTab } from "@/components/palgame/ArenaTab";
import { ExpeditionTab } from "@/components/palgame/ExpeditionTab";
import { TradeTab } from "@/components/palgame/TradeTab";
import { PvPTab } from "@/components/palgame/PvPTab";
import { LeaderboardTab } from "@/components/palgame/LeaderboardTab";
import { CareTab } from "@/components/palgame/CareTab";
import { HelpTab } from "@/components/palgame/HelpTab";

const TABS = [
  { id: "catch", label: "Catch", icon: Target },
  { id: "box", label: "Pal Box", icon: Layers },
  { id: "breed", label: "Breed", icon: Zap },
  { id: "care", label: "Pal Care", icon: Stethoscope },
  { id: "adventure", label: "Adventure", icon: Compass },
  { id: "expedition", label: "Expedition", icon: Map },
  { id: "trade", label: "Trade", icon: ArrowLeftRight },
  { id: "battle", label: "Battle", icon: Swords },
  { id: "arena", label: "Arena", icon: Castle },
  { id: "pvp", label: "PvP", icon: Crosshair },
  { id: "economy", label: "Economy", icon: Coins },
  { id: "leaderboard", label: "Top", icon: Trophy },
  { id: "help", label: "How It Works", icon: BookOpen },
];

export default function PalGame() {
  const [params] = useSearchParams();
  // Seed the active tab from `?tab=care` so the /pal-care → /palgame
  // redirect lands the user directly on the Pal Care surgery table.
  const initialTab = params.get("tab");
  const [tab, setTab] = useState(
    TABS.some((t) => t.id === initialTab) ? initialTab : "catch"
  );
  // Sidebar deep-links like /palgame?tab=economy ("Work") change the
  // search params *without* unmounting PalGame, so we need to sync
  // the tab state on each URL change — not just on first mount.
  useEffect(() => {
    const t = params.get("tab");
    if (t && TABS.some((x) => x.id === t)) {
      setTab(t);
    }
  }, [params]);
  // Shared cinematic for every payout-emitting tab (Catch, Adventure,
  // Arena, Expedition, PvP, Battle). Sub-components dispatch a window
  // event with `{amount, label}` and we render the burst centrally so
  // the celebration is identical to Gambling wins.
  const [flash, setFlash] = useState(null);
  const [defeatFlash, setDefeatFlash] = useState(null);
  useEffect(() => {
    const onPayout = (e) => {
      const { amount, label } = e.detail || {};
      if (!amount) return;
      setFlash({ amount, label: label || "REWARD" });
    };
    const onDefeat = (e) => {
      const { amount, label } = e.detail || {};
      setDefeatFlash({ amount: amount || 0, label: label || "DEFEAT" });
    };
    window.addEventListener("chillets:palgame-payout", onPayout);
    window.addEventListener("chillets:palgame-defeat", onDefeat);
    return () => {
      window.removeEventListener("chillets:palgame-payout", onPayout);
      window.removeEventListener("chillets:palgame-defeat", onDefeat);
    };
  }, []);

  return (
    <div>
      <PageHeader title="Pal Game" subtitle="Catch, battle, and breed your way to legend." />
      <div className="flex flex-wrap gap-1 mb-6 border-b border-white/5" data-testid="palgame-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`palgame-tab-${t.id}`}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 text-xs uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${
              tab === t.id ? "border-[var(--teal)] text-[var(--teal)]" : "border-transparent text-[var(--muted)] hover:text-[var(--bone)]"
            }`}
          >
            <t.icon size={14} /> <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>
      {tab === "catch" && <CatchTab />}
      {tab === "box" && <BoxTab />}
      {tab === "breed" && <BreedTab />}
      {tab === "care" && <CareTab />}
      {tab === "adventure" && <AdventureTab />}
      {tab === "battle" && <BattleTab />}
      {tab === "arena" && <ArenaTab />}
      {tab === "expedition" && <ExpeditionTab />}
      {tab === "trade" && <TradeTab />}
      {tab === "pvp" && <PvPTab />}
      {tab === "economy" && <Economy embedded />}
      {tab === "leaderboard" && <LeaderboardTab />}
      {tab === "help" && <HelpTab />}

      <PayoutFlash
        show={flash !== null}
        variant="skull"
        amount={flash?.amount || 0}
        label={flash?.label || "REWARD"}
        onDone={() => setFlash(null)}
      />
      <PayoutFlash
        show={defeatFlash !== null}
        variant="defeat"
        amount={defeatFlash?.amount || 0}
        label={defeatFlash?.label || "DEFEAT"}
        duration={1400}
        onDone={() => setDefeatFlash(null)}
      />
    </div>
  );
}
